
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigUpdateRequest} from './ConfigUpdateRequest';
import {FieldModelConfigUpdateRequest} from './ConfigUpdateRequest';
import {FirewallRuleDeleteResponse} from './FirewallRuleDeleteResponse';

/**
 * FirewallRuleDeleteRequest struct
 */
class FirewallRuleDeleteRequest extends ConfigUpdateRequest {
  /**
   * Initialize struct
   * @param {!ConfigUpdateRequest=} parent
   * @param {UUID=} firewallRuleId
   * @param {string=} firewallRuleName
   * @constructor
   */
  constructor (parent = new ConfigUpdateRequest(), firewallRuleId = null, firewallRuleName = null) {
    super()
    super.copy(parent)
    this.FirewallRuleId = firewallRuleId
    this.FirewallRuleName = firewallRuleName
  }

  /**
   * Copy struct (shallow copy)
   * @this {!FirewallRuleDeleteRequest}
   * @param {!FirewallRuleDeleteRequest} other Other struct
   * @returns {!FirewallRuleDeleteRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.FirewallRuleId != null) {
      this.FirewallRuleId = new UUID(other.FirewallRuleId)
    } else {
      this.FirewallRuleId = null
    }
    if (other.FirewallRuleName != null) {
      this.FirewallRuleName = other.FirewallRuleName
    } else {
      this.FirewallRuleName = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!FirewallRuleDeleteRequest}
   * @returns {!FirewallRuleDeleteRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new FirewallRuleDeleteRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new FirewallRuleDeleteRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!FirewallRuleDeleteRequest}
   * @param {!FirewallRuleDeleteRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof FirewallRuleDeleteRequest)) {
      throw new TypeError('Instance of FirewallRuleDeleteRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!FirewallRuleDeleteRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      FirewallRuleId: ((this.FirewallRuleId != null) ? this.FirewallRuleId.toString() : null),
      FirewallRuleName: ((this.FirewallRuleName != null) ? this.FirewallRuleName : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return FirewallRuleDeleteRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!FirewallRuleDeleteRequest} other Object value
   * @returns {!FirewallRuleDeleteRequest} Created struct
   */
  static fromObject (other) {
    return new FirewallRuleDeleteRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!FirewallRuleDeleteRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return FirewallRuleDeleteRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!FirewallRuleDeleteRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6551
  }
}

export { FirewallRuleDeleteRequest };

/**
 * Fast Binary Encoding FirewallRuleDeleteRequest field model
 */
class FieldModelFirewallRuleDeleteRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigUpdateRequest(buffer, 4 + 4)
    this.FirewallRuleId = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.FirewallRuleName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.FirewallRuleId.fbeOffset + this.FirewallRuleId.fbeSize), buffer, this.FirewallRuleId.fbeOffset + this.FirewallRuleId.fbeSize)
  }

  /**
   * Get the ConfigUpdateRequest field model
   * @this {!FieldModelFirewallRuleDeleteRequest}
   * @returns {!FieldModelConfigUpdateRequest} ConfigUpdateRequest field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelFirewallRuleDeleteRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelFirewallRuleDeleteRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.FirewallRuleId.fbeSize + this.FirewallRuleName.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelFirewallRuleDeleteRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.FirewallRuleId.fbeExtra + this.FirewallRuleName.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelFirewallRuleDeleteRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelFirewallRuleDeleteRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelFirewallRuleDeleteRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6551
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelFirewallRuleDeleteRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelFirewallRuleDeleteRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.FirewallRuleId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.FirewallRuleId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.FirewallRuleId.fbeSize

    if ((fbeCurrentSize + this.FirewallRuleName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.FirewallRuleName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.FirewallRuleName.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelFirewallRuleDeleteRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelFirewallRuleDeleteRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelFirewallRuleDeleteRequest}
   * @param {!FirewallRuleDeleteRequest} fbeValue Default value, defaults is new FirewallRuleDeleteRequest()
   * @returns {!FirewallRuleDeleteRequest} FirewallRuleDeleteRequest value
   */
  get (fbeValue = new FirewallRuleDeleteRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelFirewallRuleDeleteRequest}
   * @param {!FirewallRuleDeleteRequest} fbeValue FirewallRuleDeleteRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.FirewallRuleId.fbeSize) <= fbeStructSize) {
      fbeValue.FirewallRuleId = this.FirewallRuleId.get()
    } else {
      fbeValue.FirewallRuleId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.FirewallRuleId.fbeSize

    if ((fbeCurrentSize + this.FirewallRuleName.fbeSize) <= fbeStructSize) {
      fbeValue.FirewallRuleName = this.FirewallRuleName.get()
    } else {
      fbeValue.FirewallRuleName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.FirewallRuleName.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelFirewallRuleDeleteRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelFirewallRuleDeleteRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelFirewallRuleDeleteRequest}
   * @param {!FirewallRuleDeleteRequest} fbeValue FirewallRuleDeleteRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelFirewallRuleDeleteRequest}
   * @param {FirewallRuleDeleteRequest} fbeValue FirewallRuleDeleteRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.FirewallRuleId.set(fbeValue.FirewallRuleId)
    this.FirewallRuleName.set(fbeValue.FirewallRuleName)
  }
}

export { FieldModelFirewallRuleDeleteRequest };

/**
 * Fast Binary Encoding FirewallRuleDeleteRequest model
 */
class FirewallRuleDeleteRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelFirewallRuleDeleteRequest(this.buffer, 4)
  }

  /**
   * Get the FirewallRuleDeleteRequest model
   * @this {!FirewallRuleDeleteRequestModel}
   * @returns {!FieldModelFirewallRuleDeleteRequest} model FirewallRuleDeleteRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!FirewallRuleDeleteRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!FirewallRuleDeleteRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return FirewallRuleDeleteRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!FirewallRuleDeleteRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelFirewallRuleDeleteRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!FirewallRuleDeleteRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!FirewallRuleDeleteRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!FirewallRuleDeleteRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!FirewallRuleDeleteRequestModel}
   * @param {!FirewallRuleDeleteRequest} value FirewallRuleDeleteRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!FirewallRuleDeleteRequestModel}
   * @param {!FirewallRuleDeleteRequest} value FirewallRuleDeleteRequest value, defaults is new FirewallRuleDeleteRequest()
   * @return {!object} Deserialized FirewallRuleDeleteRequest value and its size
   */
  deserialize (value = new FirewallRuleDeleteRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new FirewallRuleDeleteRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new FirewallRuleDeleteRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!FirewallRuleDeleteRequestModel}
   * @param {!number} prev Previous FirewallRuleDeleteRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { FirewallRuleDeleteRequestModel };
FirewallRuleDeleteRequest.__has_response = true;
FirewallRuleDeleteRequest.__response_class = FirewallRuleDeleteResponse;
